import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import MadPropsCarousel from "../components/MadPropsCarousel";
import config from "../config";
import "./MattDashboard.css";

const POLL_INTERVAL = 20 * 60 * 1000; // 20 minutes

// ── Status labels ──
const STATUS_LABELS = {
  pct_other: "Internal Review",
  pct_in_queue: "In Queue",
  pct_ready: "Ready",
  pct_in_process: "In Process",
  pct_finished: "Finished",
  pct_closed: "Closed",
};

const STATUS_ORDER = [
  "pct_in_queue",
  "pct_ready",
  "pct_in_process",
  "pct_other",
  "pct_finished",
  "pct_closed",
];

// ── Ideal percentages by cycle day (1-14) ──
const IDEAL_BY_DAY = {
  1:  { pct_other: 10, pct_in_queue: 40, pct_ready: 25, pct_in_process: 20, pct_finished:  5, pct_closed:  0 },
  2:  { pct_other:  8, pct_in_queue: 35, pct_ready: 25, pct_in_process: 25, pct_finished:  7, pct_closed:  0 },
  3:  { pct_other:  6, pct_in_queue: 28, pct_ready: 22, pct_in_process: 30, pct_finished: 12, pct_closed:  2 },
  4:  { pct_other:  5, pct_in_queue: 22, pct_ready: 18, pct_in_process: 32, pct_finished: 18, pct_closed:  5 },
  5:  { pct_other:  4, pct_in_queue: 16, pct_ready: 15, pct_in_process: 30, pct_finished: 25, pct_closed: 10 },
  6:  { pct_other:  3, pct_in_queue: 12, pct_ready: 12, pct_in_process: 28, pct_finished: 28, pct_closed: 17 },
  7:  { pct_other:  2, pct_in_queue:  8, pct_ready: 10, pct_in_process: 25, pct_finished: 30, pct_closed: 25 },
  8:  { pct_other:  2, pct_in_queue:  5, pct_ready:  8, pct_in_process: 20, pct_finished: 30, pct_closed: 35 },
  9:  { pct_other:  1, pct_in_queue:  3, pct_ready:  5, pct_in_process: 16, pct_finished: 28, pct_closed: 47 },
  10: { pct_other:  1, pct_in_queue:  2, pct_ready:  3, pct_in_process: 12, pct_finished: 22, pct_closed: 60 },
  11: { pct_other:  0, pct_in_queue:  1, pct_ready:  2, pct_in_process:  8, pct_finished: 17, pct_closed: 72 },
  12: { pct_other:  0, pct_in_queue:  0, pct_ready:  1, pct_in_process:  5, pct_finished: 12, pct_closed: 82 },
  13: { pct_other:  0, pct_in_queue:  0, pct_ready:  0, pct_in_process:  3, pct_finished:  7, pct_closed: 90 },
  14: { pct_other:  0, pct_in_queue:  0, pct_ready:  0, pct_in_process:  0, pct_finished:  2, pct_closed: 98 },
};

const HIGHER_IS_BETTER = new Set(["pct_in_process", "pct_finished", "pct_closed"]);
const LOWER_IS_BETTER = new Set(["pct_other", "pct_in_queue", "pct_ready"]);

const getCycleDay = (wsNum, currentCycles) => {
  if (!currentCycles || currentCycles.length === 0) return null;
  const cycle = currentCycles.find(
    (c) => String(c.workstream_num) === String(wsNum)
  );
  if (!cycle || !cycle.cycle_start || !cycle.cycle_end) return null;
  const start = new Date(cycle.cycle_start);
  const now = new Date();
  const diffMs = now - start;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(1, Math.min(14, diffDays + 1));
};

const interpolateColor = (score) => {
  const s = Math.max(0, Math.min(1, score));
  if (s <= 0.5) {
    const t = s / 0.5;
    return `rgb(220, ${Math.round(50 + t * 150)}, 30)`;
  }
  const t = (s - 0.5) / 0.5;
  return `rgb(${Math.round(220 - t * 185)}, ${Math.round(200 - t * 30)}, ${Math.round(30 + t * 70)})`;
};

const getHealthColor = (key, pct, cycleDay) => {
  if (pct === 0) return undefined;
  const day = cycleDay || 7;
  const ideal = IDEAL_BY_DAY[day]?.[key];
  if (ideal === undefined) return "var(--text-primary, #223651)";

  if (HIGHER_IS_BETTER.has(key)) {
    if (ideal === 0) return interpolateColor(1);
    const score = Math.min(pct / ideal, 1);
    return interpolateColor(score);
  }

  if (LOWER_IS_BETTER.has(key)) {
    if (ideal === 0) {
      return pct > 0 ? interpolateColor(0) : interpolateColor(1);
    }
    const score = Math.min(1, 1 - (pct - ideal) / ideal);
    return interpolateColor(Math.max(0, score));
  }

  return "var(--text-primary, #223651)";
};

// ── Hours display helper ──
const formatHours = (hours) => {
  if (!hours) return null;
  const h = parseFloat(hours);
  if (h === 8) return null;
  if (h === 4) return <span className="matt-person-detail">½</span>;
  return <span className="matt-person-detail">{h}h</span>;
};

// ── People Card with optional vertical carousel ──
const SCROLL_SPEED = 0.3;

const PeopleCard = ({ title, icon, items, nameKey, color, emptyMsg }) => {
  const listRef = useRef(null);
  const innerRef = useRef(null);
  const offsetRef = useRef(0);
  const animRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  const displayItems = useMemo(() => {
    if (!overflows) return items;
    return [...items, ...items];
  }, [items, overflows]);

  useEffect(() => {
    const checkOverflow = () => {
      if (listRef.current && innerRef.current) {
        const containerH = listRef.current.clientHeight;
        const contentH = innerRef.current.scrollHeight / (overflows ? 2 : 1);
        setOverflows(contentH > containerH);
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [items, overflows]);

  useEffect(() => {
    if (!overflows || !innerRef.current) return;
    const singleSetHeight = innerRef.current.scrollHeight / 2;
    if (singleSetHeight <= 0) return;
    offsetRef.current = 0;

    const animate = () => {
      offsetRef.current += SCROLL_SPEED;
      if (offsetRef.current >= singleSetHeight) {
        offsetRef.current -= singleSetHeight;
      }
      if (innerRef.current) {
        innerRef.current.style.transform = `translateY(${-offsetRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [overflows]);

  return (
    <div className="matt-card" style={{ borderTop: `3px solid ${color}` }}>
      <div className="matt-card-header">
        <span className="matt-card-icon">{icon}</span>
        <h3 className="matt-card-title">{title}</h3>
        <span className="matt-card-count" style={{ background: color }}>
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="matt-card-empty">{emptyMsg}</div>
      ) : (
        <div
          className={`matt-people-list-container ${overflows ? "matt-people-list-masked" : ""}`}
          ref={listRef}
        >
          <ul className="matt-people-list" ref={innerRef}>
            {displayItems.map((item, i) => (
              <li key={i} className="matt-person">
                <span className="matt-person-name">{item[nameKey]}</span>
                {formatHours(item.hours)}
                {item.title && (
                  <span className="matt-person-detail">{item.title}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Birthdays & Anniversaries Week View ──
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const BirthdaysAnniversaries = ({ data }) => {
  // useMemo so `items` keeps a stable identity when data?.items hasn't
  // changed — otherwise the dependent `byDay` useMemo invalidates every render.
  const items = useMemo(() => data?.items || [], [data?.items]);
  const todayIndex = data?.today_index ?? new Date().getDay();
  const weekDates = data?.week_dates || [];

  // Group items by day_index
  const byDay = useMemo(() => {
    const grouped = Array.from({ length: 7 }, () => []);
    items.forEach((item) => {
      const idx = item.day_index;
      if (idx >= 0 && idx < 7) {
        grouped[idx].push(item);
      }
    });
    return grouped;
  }, [items]);

  // Format month-day for column header
  const formatDate = (mmdd) => {
    if (!mmdd) return "";
    const [m, d] = mmdd.split("-");
    return `${parseInt(m)}/${parseInt(d)}`;
  };

  return (
    <div
      className="matt-card matt-bday-card"
      style={{ borderTop: "3px solid #ec4899" }}
    >
      <div className="matt-card-header">
        <span className="matt-card-icon">🎂</span>
        <h3 className="matt-card-title">Birthdays & Anniversaries</h3>
        <span className="matt-card-count" style={{ background: "#ec4899" }}>
          {items.length}
        </span>
      </div>
      <div className="matt-bday-week">
        {DAY_NAMES.map((dayName, idx) => (
          <div
            key={idx}
            className={`matt-bday-day ${idx === todayIndex ? "matt-bday-day-today" : ""}`}
          >
            <div className="matt-bday-day-header">
              <span className="matt-bday-day-name">{dayName}</span>
              {weekDates[idx] && (
                <span className="matt-bday-day-date">
                  {formatDate(weekDates[idx])}
                </span>
              )}
            </div>
            <div className="matt-bday-day-items">
              {byDay[idx].length === 0 ? (
                <div className="matt-bday-empty">—</div>
              ) : (
                byDay[idx].map((item, i) => (
                  <div key={i} className="matt-bday-item">
                    <span className="matt-bday-icon">
                      {item.event_type === "birthday" ? "🎂" : "🎉"}
                    </span>
                    <div className="matt-bday-info">
                      <span className="matt-bday-name">{item.full_name}</span>
                      {item.event_type === "anniversary" &&
                        item.years_at_company && (
                          <span className="matt-bday-years">
                            {item.years_at_company} yr{item.years_at_company !== 1 ? "s" : ""}
                          </span>
                        )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Workstream Revenue Table ──
const WorkstreamTable = ({ data, currentCycles }) => (
  <div
    className="matt-card matt-workstream-card"
    style={{ borderTop: "3px solid #3b82f6" }}
  >
    <div className="matt-card-header">
      <span className="matt-card-icon">📊</span>
      <h3 className="matt-card-title">Workstream Revenue by Status</h3>
    </div>
    {!data || data.length === 0 ? (
      <div className="matt-card-empty">No workstream data available</div>
    ) : (
      <div className="matt-ws-table-wrapper">
        <table className="matt-ws-table">
          <thead>
            <tr>
              <th className="matt-ws-th matt-ws-th-ws">Workstream</th>
              {STATUS_ORDER.map((key) => (
                <th key={key} className="matt-ws-th">
                  {STATUS_LABELS[key]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((ws, i) => {
              const day = getCycleDay(ws.workstream_num, currentCycles);
              return (
                <tr key={i} className="matt-ws-tr">
                  <td className="matt-ws-td matt-ws-td-ws">
                    WS {ws.workstream_num}
                    {day && <span className="matt-ws-day">Day {day}</span>}
                  </td>
                  {STATUS_ORDER.map((key) => {
                    const pct = parseFloat(ws[key]) || 0;
                    return (
                      <td key={key} className="matt-ws-td">
                        <span
                          className="matt-ws-pct"
                          style={{ color: getHealthColor(key, pct, day) }}
                        >
                          {pct > 0 ? `${pct}%` : "—"}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ── Role Revenue by Status (split by workstream + swimlane) ──
const ROLE_SCROLL_SPEED = 0.35; // px per frame — slow, smooth

const RoleRevenueTable = ({ data, currentCycles }) => {
  const wrapperRef = useRef(null);
  const tableRef = useRef(null);
  const scrollRef = useRef(0);
  const animRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  // useMemo so `rows` keeps a stable identity when `data` hasn't changed,
  // preventing the dependent useMemo / useEffect below from invalidating
  // every render.
  const rows = useMemo(() => data || [], [data]);
  const byWs = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const k = String(r.workstream_num);
      if (!m[k]) m[k] = [];
      m[k].push(r);
    }
    return m;
  }, [rows]);
  const wsKeys = useMemo(
    () => Object.keys(byWs).sort((a, b) => Number(a) - Number(b)),
    [byWs]
  );

  // Measure content vs container to decide whether to animate.
  useEffect(() => {
    const check = () => {
      if (!wrapperRef.current || !tableRef.current) return;
      const wrapH = wrapperRef.current.clientHeight;
      const contentH = tableRef.current.scrollHeight / (overflows ? 2 : 1);
      setOverflows(contentH > wrapH + 2);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [rows, overflows]);

  // Smoothly auto-scroll via the real scrollTop so position:sticky works.
  useEffect(() => {
    if (!overflows || !wrapperRef.current || !tableRef.current) return;

    // singleH must be the height of *one* duplicated copy. The <thead> is not
    // duplicated, so we measure the <tbody> (which contains both copies) and
    // halve it. Using tableRef.scrollHeight/2 would over-count by thead/2 and
    // cause the non-sticky rows to jump at every wrap.
    const tbody = tableRef.current.querySelector("tbody");
    const singleH = tbody
      ? tbody.scrollHeight / 2
      : tableRef.current.scrollHeight / 2;
    if (singleH <= 0) return;

    // Start at the scrollTop where the first WS label is already pinned to
    // its sticky top, so the wrap-around doesn't produce a visible jump.
    const STICKY_TOP = 28;
    const thead = tableRef.current.querySelector("thead");
    const theadH = thead ? thead.getBoundingClientRect().height : 0;
    const firstTd = tableRef.current.querySelector(".matt-role-td-ws");
    const tdPadTop = firstTd
      ? parseFloat(window.getComputedStyle(firstTd).paddingTop) || 0
      : 0;
    const baseOffset = Math.max(0, theadH + tdPadTop - STICKY_TOP);

    scrollRef.current = baseOffset;
    wrapperRef.current.scrollTop = baseOffset;

    const animate = () => {
      scrollRef.current += ROLE_SCROLL_SPEED;
      if (scrollRef.current - baseOffset >= singleH) {
        scrollRef.current -= singleH;
      }
      if (wrapperRef.current) {
        wrapperRef.current.scrollTop = scrollRef.current;
      }
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [overflows]);

  const renderGroups = (prefix) =>
    wsKeys.map((wsNum) => {
      const day = getCycleDay(wsNum, currentCycles);
      const group = byWs[wsNum];
      return group.map((row, idx) => (
        <tr key={`${prefix}-${wsNum}-${idx}`} className="matt-role-tr">
          {idx === 0 && (
            <td
              className="matt-role-td matt-role-td-ws"
              rowSpan={group.length}
            >
              <span className="matt-role-td-ws-inner">
                WS {wsNum}
                {day && <span className="matt-ws-day">Day {day}</span>}
              </span>
            </td>
          )}
          <td className="matt-role-td matt-role-td-name">{row.name}</td>
          {STATUS_ORDER.map((key) => {
            const pct = parseFloat(row[key]) || 0;
            return (
              <td key={key} className="matt-role-td">
                <span
                  className="matt-role-pct"
                  style={{ color: getHealthColor(key, pct, day) }}
                >
                  {pct > 0 ? `${pct}%` : "—"}
                </span>
              </td>
            );
          })}
        </tr>
      ));
    });

  return (
    <div className="matt-role-table-wrapper" ref={wrapperRef}>
      {rows.length === 0 ? (
        <div className="matt-card-empty">No role data available</div>
      ) : (
        <table className="matt-role-table" ref={tableRef}>
          <thead>
            <tr>
              <th className="matt-role-th matt-role-th-ws">WS</th>
              <th className="matt-role-th matt-role-th-name">Role</th>
              {STATUS_ORDER.map((key) => (
                <th key={key} className="matt-role-th">
                  {STATUS_LABELS[key]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderGroups("a")}
            {overflows && renderGroups("b")}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Client Commitments ──
const COMMIT_SCROLL_SPEED = 0.075;

// Independently scrolling vertical list; only animates when content overflows.
const ScrollingLane = ({ items, renderRow, emptyMsg, rowClass }) => {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const offsetRef = useRef(0);
  const animRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  const displayItems = useMemo(() => {
    if (!overflows) return items;
    return [...items, ...items];
  }, [items, overflows]);

  useEffect(() => {
    const check = () => {
      if (containerRef.current && innerRef.current) {
        const containerH = containerRef.current.clientHeight;
        const contentH = innerRef.current.scrollHeight / (overflows ? 2 : 1);
        setOverflows(contentH > containerH);
      }
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [items, overflows]);

  useEffect(() => {
    if (!overflows || !innerRef.current) return;
    const singleH = innerRef.current.scrollHeight / 2;
    if (singleH <= 0) return;
    offsetRef.current = 0;

    const animate = () => {
      offsetRef.current += COMMIT_SCROLL_SPEED;
      if (offsetRef.current >= singleH) offsetRef.current -= singleH;
      if (innerRef.current) {
        innerRef.current.style.transform = `translateY(${-offsetRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [overflows]);

  return (
    <div
      className={`matt-commit-scroll ${overflows ? "matt-commit-scroll-masked" : ""}`}
      ref={containerRef}
    >
      <div ref={innerRef} className="matt-commit-inner">
        {items.length === 0 ? (
          <div className="matt-card-empty">{emptyMsg}</div>
        ) : (
          displayItems.map((item, i) => (
            <div key={i} className={rowClass}>{renderRow(item)}</div>
          ))
        )}
      </div>
    </div>
  );
};

const CommitColumn = ({ items, rightItems, wsNum, currentCycles }) => {
  const cycleName = () => {
    const cycle = (currentCycles || []).find(
      (c) => String(c.workstream_num) === String(wsNum)
    );
    return cycle ? cycle.name : `WS ${wsNum}`;
  };

  const expectedPct = 100;

  const renderLeft = (row) => {
    const pctStr = row.commitment_percent;
    const pct = pctStr != null ? parseFloat(pctStr) : null;
    let color;
    if (pct === null || isNaN(pct)) {
      color = undefined;
    } else if (expectedPct === 0) {
      color = interpolateColor(1);
    } else {
      color = interpolateColor(Math.min(pct / expectedPct, 1));
    }
    return (
      <>
        <span className="matt-commit-name">{row.program_name}</span>
        <span className="matt-commit-pct" style={{ color }}>
          {pctStr || "—"}
        </span>
      </>
    );
  };

  const renderRight = (row) => {
    const label = row?.swimlane || row?.label || row?.program_name || "—";
    const pctStr = row?.commitment_percent ?? row?.percent;
    const pct = pctStr != null ? parseFloat(pctStr) : null;
    let color;
    if (pct === null || isNaN(pct)) {
      color = undefined;
    } else {
      color = interpolateColor(Math.min(pct / 100, 1));
    }
    return (
      <>
        <span className="matt-commit-right-label">{label}</span>
        <span className="matt-commit-right-pct" style={{ color }}>
          {pctStr || "—"}
        </span>
      </>
    );
  };

  return (
    <div className="matt-commit-col">
      <h3 className="matt-commit-col-title">{cycleName()}</h3>
      <div className="matt-commit-th-row">
        <div className="matt-commit-th-half">
          <span className="matt-commit-th-label">Client</span>
          <span className="matt-commit-th-label matt-commit-th-label-right">Achieved</span>
        </div>
        <span className="matt-commit-th-divider" />
        <div className="matt-commit-th-half">
          <span className="matt-commit-th-label">Role Type</span>
          <span className="matt-commit-th-label matt-commit-th-label-right">Achieved</span>
        </div>
      </div>
      <div className="matt-commit-lanes">
        <ScrollingLane
          items={items || []}
          renderRow={renderLeft}
          emptyMsg="No commitments"
          rowClass="matt-commit-left-row"
        />
        <div className="matt-commit-lane-divider" />
        <ScrollingLane
          items={rightItems || []}
          renderRow={renderRight}
          emptyMsg="—"
          rowClass="matt-commit-right-row"
        />
      </div>
    </div>
  );
};

const ClientCommitments = ({ data, rightData, currentCycles }) => {
  const isActive = (d) => d.commitment_percent != null && parseFloat(d.commitment_percent) > 0;
  const ws1 = (data || []).filter((d) => String(d.workstream_num) === "1" && isActive(d));
  const ws2 = (data || []).filter((d) => String(d.workstream_num) === "2" && isActive(d));
  const sortBySwimlaneAsc = (a, b) =>
    String(a?.swimlane || "").localeCompare(String(b?.swimlane || ""));
  const ws1Right = (rightData || [])
    .filter((d) => String(d.workstream_num) === "1")
    .sort(sortBySwimlaneAsc);
  const ws2Right = (rightData || [])
    .filter((d) => String(d.workstream_num) === "2")
    .sort(sortBySwimlaneAsc);

  return (
    <div
      className="matt-card matt-commitments-card"
      style={{ borderTop: "3px solid #6366f1" }}
    >
      <div className="matt-card-header">
        <span className="matt-card-icon">🎯</span>
        <h3 className="matt-card-title">
          Client Commitments <span className="matt-card-title-sub">(Previous Cycle)</span>
        </h3>
      </div>
      <div className="matt-commit-columns">
        <CommitColumn items={ws1} rightItems={ws1Right} wsNum={1} currentCycles={currentCycles} />
        <div className="matt-commit-divider" />
        <CommitColumn items={ws2} rightItems={ws2Right} wsNum={2} currentCycles={currentCycles} />
      </div>
    </div>
  );
};

// ── Horizontal Carousel ──
const CAROUSEL_SPEED = 0.3; // px per frame — very slow

const HorizontalCarousel = ({ children }) => {
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const animRef = useRef(null);
  const [totalWidth, setTotalWidth] = useState(0);
  const panelOffsetsRef = useRef([]);
  const [paused, setPaused] = useState(false);

  // Measure the single set width and record each panel's left offset.
  useEffect(() => {
    if (!trackRef.current) return;
    const panels = trackRef.current.children;
    const half = Math.floor(panels.length / 2);
    const offsets = [];
    let w = 0;
    for (let i = 0; i < half; i++) {
      offsets.push(w);
      w += panels[i].offsetWidth;
    }
    panelOffsetsRef.current = offsets;
    setTotalWidth(w);
  }, [children]);

  // Reset scroll position only when content width changes.
  useEffect(() => {
    offsetRef.current = 0;
  }, [totalWidth]);

  // Auto-scroll animation; pauses when `paused` is true.
  useEffect(() => {
    if (totalWidth <= 0 || !trackRef.current || paused) return;

    const animate = () => {
      offsetRef.current += CAROUSEL_SPEED;
      if (offsetRef.current >= totalWidth) offsetRef.current -= totalWidth;
      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(${-offsetRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [totalWidth, paused]);

  // Dev/testing: ←/→ jumps to prev/next panel and pauses. Space toggles auto-scroll.
  useEffect(() => {
    const handleKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;

      const offsets = panelOffsetsRef.current;
      if (totalWidth <= 0 || offsets.length === 0) return;

      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        setPaused(true);

        const current = ((offsetRef.current % totalWidth) + totalWidth) % totalWidth;
        let target;
        if (e.key === "ArrowRight") {
          target = offsets.find((o) => o > current + 1);
          if (target == null) target = offsets[0];
        } else {
          target = null;
          for (let i = offsets.length - 1; i >= 0; i--) {
            if (offsets[i] < current - 1) { target = offsets[i]; break; }
          }
          if (target == null) target = offsets[offsets.length - 1];
        }
        offsetRef.current = target;
        if (trackRef.current) {
          trackRef.current.style.transform = `translateX(${-offsetRef.current}px)`;
        }
      } else if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        setPaused((p) => !p);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [totalWidth]);

  // Duplicate children for seamless wrap
  return (
    <div className="matt-carousel-viewport">
      <div className="matt-carousel-track" ref={trackRef}>
        {children}
        {children}
      </div>
    </div>
  );
};

const MattDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.opsDashboard.data}`,
        { method: "GET", credentials: "include" }
      );
      if (!response.ok) throw new Error("Failed to fetch dashboard data");
      const json = await response.json();
      if (json.success && json.data) {
        setData(json.data);
        setError(null);
      }
    } catch (err) {
      console.error("Error fetching ops dashboard data:", err);
      if (!data) setError("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) {
    return (
      <div className="matt-dashboard">
        <div className="matt-loading-full">Loading dashboard...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="matt-dashboard">
        <div className="matt-loading-full">{error}</div>
      </div>
    );
  }

  return (
    <div className="matt-dashboard">
      <HorizontalCarousel>
        {/* ── Panel 1: PTO/WFH/Birthdays left, Mad Props right, Workstream bottom ── */}
        <div className="matt-carousel-panel matt-panel-main">
          <div className="matt-panel-top">
            <div className="matt-top-left">
              <div className="matt-people-grid">
                <PeopleCard
                  title="Out Today (PTO)"
                  icon="🏖️"
                  items={data?.pto_today || []}
                  nameKey="person"
                  color="#ef4444"
                  emptyMsg="No one on PTO today"
                />
                <PeopleCard
                  title="Working From Home"
                  icon="🏠"
                  items={data?.wfh_today || []}
                  nameKey="person"
                  color="#f59e0b"
                  emptyMsg="No one WFH today"
                />
              </div>
              <BirthdaysAnniversaries
                data={data?.birthdays_anniversaries_week || {}}
              />
            </div>
            <div className="matt-top-right">
              <div className="matt-placeholder-wrapper">
                <h2 className="matt-section-title">Revenue by Role</h2>
                <RoleRevenueTable
                  data={data?.role_revenue_pct_by_status || []}
                  currentCycles={data?.current_cycles || []}
                />
              </div>
              <div className="matt-mad-prmatt-wrapper">
                <h2 className="matt-section-title">Mad Props</h2>
                <MadPropsCarousel
                  messages={data?.mad_props?.messages || []}
                  reverse
                  speed={CAROUSEL_SPEED}
                />
              </div>
            </div>
          </div>
          <div className="matt-panel-bottom">
            <WorkstreamTable
              data={data?.workstream_revenue_pct_by_status || []}
              currentCycles={data?.current_cycles || []}
            />
          </div>
        </div>

        {/* ── Panel 2: Client Commitments (tall) ── */}
        <div className="matt-carousel-panel matt-panel-commitments">
          <ClientCommitments
            data={data?.client_commitments || []}
            rightData={data?.swimlane_commitments || []}
            currentCycles={data?.current_cycles || []}
          />
        </div>
      </HorizontalCarousel>
    </div>
  );
};

export default MattDashboard;
